// The sandbox of a batch as a React hook (LH-F5): opens it on the agent when a page needs it, reports offline /
// opening / ready, re-opens when the batch version changes, and gives the `invoke` for Execute and test calls.
import { useCallback, useEffect, useRef, useState } from 'react'
import { AgentOffline, getAgent, sandboxId, type SandboxInfo } from '@/lib/agent'
import { invokeFor, openSandbox, sandboxEnv } from '@/lib/sandbox'
import type { Invoke } from '@/lib/flowEngine'
import { useSettingsStore } from '@/store/settingsStore'
import type { Batch, Lender } from '@/types'
import { useCurrentUser } from '@/shell/useCurrentUser'
import { codeKeyOf } from '@/lib/batch'

export type SandboxState = { status: 'idle' | 'opening' | 'ready' | 'offline' | 'error'; info: SandboxInfo | null; error: string | null }

export function useSandbox(lender: Lender, batch: Batch | undefined, opts: { auto?: boolean; environment?: 'uat' | 'production' } = {}) {
  const { user } = useCurrentUser()
  const settings = useSettingsStore((s) => s.settings)
  const [state, setState] = useState<SandboxState>({ status: 'idle', info: null, error: null })
  const environment = opts.environment ?? 'uat'
  const opening = useRef<Promise<SandboxInfo | null> | null>(null)
  const batchId = batch?.id

  const open = useCallback(
    async (reset = false): Promise<SandboxInfo | null> => {
      if (!batch) return null
      if (opening.current && !reset) return opening.current
      setState((s) => ({ ...s, status: 'opening', error: null }))
      const p = openSandbox(lender, batch, { user, settings, environment, reset })
        .then((info) => {
          setState({ status: 'ready', info, error: null })
          return info
        })
        .catch((err: unknown) => {
          const offline = err instanceof AgentOffline
          setState({ status: offline ? 'offline' : 'error', info: null, error: err instanceof Error ? err.message : String(err) })
          return null
        })
        .finally(() => {
          opening.current = null
        })
      opening.current = p
      return p
    },
    [lender, batch, user, settings, environment], // eslint-disable-line react-hooks/exhaustive-deps
  )

  useEffect(() => {
    if (opts.auto !== false && batch) void open()
    else setState({ status: 'idle', info: null, error: null })
  }, [batchId, environment]) // eslint-disable-line react-hooks/exhaustive-deps

  /** the lender's variables changed: write .env again */
  const refreshEnv = useCallback(async () => {
    if (!batch || state.status !== 'ready') return
    await getAgent().env(sandboxId(codeKeyOf(batch), batch.version), await sandboxEnv(lender, environment), environment)
  }, [lender, batch, environment, state.status])

  const invoke: Invoke | null = batch && state.status === 'ready' ? invokeFor(lender, batch, environment) : null
  const id = batch ? sandboxId(codeKeyOf(batch), batch.version) : null
  return { ...state, id, open, refreshEnv, invoke, agent: getAgent() }
}
